import * as React from "react";
import { UpiPaymentMethod } from "@swiggy-private/payment-ui";
import { useMountedRef, captureError } from "@swiggy-private/core-ui";

// api
import { ApiContext } from "@swiggy-private/checkout-core";
import { useOnPay } from "./UseOnPay";
import { verifyVpaPublic } from "@payment-client/apis/publicPayment";

interface UseVerifyVpaReturn {
    verifyInProgress: boolean;
    invalidVpa: boolean;
    verifyVpa: (vpaAddress: string, saveVpa: boolean, methodData: UpiPaymentMethod) => Promise<void>;
}

export const useVerifyVpa = (): UseVerifyVpaReturn => {
    const isMountedRef = useMountedRef();
    const { host } = React.useContext(ApiContext);
    const [verifyInProgress, setVerifyInProgress] = React.useState(false);
    const [invalidVpa, setInvalidVpa] = React.useState(false);
    const onPay = useOnPay();

    const verifyVpa = React.useCallback<UseVerifyVpaReturn["verifyVpa"]>(
        async (vpaAddress, saveVpa, methodData) => {
            if (verifyInProgress) {
                return;
            }

            setVerifyInProgress(true);
            setInvalidVpa(false);

            try {
                // Call verify vpa address api
                const response = await verifyVpaPublic(host.unwrap(), vpaAddress);
                if (!isMountedRef.current) {
                    return;
                }

                if (!response?.vpaValid) {
                    setInvalidVpa(true);
                } else {
                    /* eslint-disable @typescript-eslint/naming-convention */
                    const upi_transaction_info = {
                        payer_vpa: vpaAddress,
                        save_instrument: saveVpa,
                        transaction_mode: methodData.meta.transactionMode ?? "",
                    };

                    onPay(methodData, { upi_transaction_info });
                    /* eslint-enable @typescript-eslint/naming-convention */
                }
            } catch (err) {
                setInvalidVpa(false);
                captureError(err);
            }

            isMountedRef.current && setVerifyInProgress(false);
        },
        [host, isMountedRef, verifyInProgress, onPay],
    );

    return {
        invalidVpa,
        verifyInProgress,
        verifyVpa,
    };
};
